import { forwardRef } from 'react';
import { FAMILY_CARD_FONT_FAMILY } from '../family-card-form/familyCardFont';
import { useFitFontSize, VALUE_INK_SHADOW } from '../shared/formFieldEmphasis';
import { buildVoterInclusionForm } from './voterInclusionFormEngine';
import {
    BASELINE_RATIO,
    EMBLEM,
    FLOW_TAIL,
    LEADER,
    LINE_BOX,
    PAGE1,
    PAGE2,
    PAGE3,
    PAGE_HEIGHT,
    PAGE_WIDTH,
    PITCH,
    TYPE,
    VALUE_INSET,
    VALUE_LIFT,
    VALUE_TYPE,
    X_SHIFT,
} from './voterInclusionMetrics';

export const VOTER_INCLUSION_PAGE_WIDTH = PAGE_WIDTH;
export const VOTER_INCLUSION_PAGE_HEIGHT = PAGE_HEIGHT;

const LEADER_TEXT = LEADER.char.repeat(LEADER.count);

const PAGE_STYLE = {
    position: 'relative',
    width: PAGE_WIDTH,
    height: PAGE_HEIGHT,
    background: '#fff',
    color: '#111',
    boxSizing: 'border-box',
    fontFamily: FAMILY_CARD_FONT_FAMILY,
    fontSize: TYPE.body.size,
    overflow: 'hidden',
};

/** A measured baseline becomes the CSS top of the line box. */
const topFor = (baseline, size) => baseline - size * BASELINE_RATIO;

const textStyle = (size, weight) => ({
    position: 'absolute',
    fontSize: size,
    fontWeight: weight,
    lineHeight: LINE_BOX,
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
});

/** One printed line, anchored by baseline and by left edge or centre. */
function Text({ item }) {
    const size = item.size ?? TYPE.body.size;
    const style = { ...textStyle(size, item.weight ?? TYPE.body.weight), top: topFor(item.y, size) };
    if (item.center != null) {
        style.left = item.center + X_SHIFT;
        style.transform = 'translateX(-50%)';
    } else {
        style.left = item.x + X_SHIFT;
    }
    if (item.ls) style.letterSpacing = item.ls;
    return <span style={style}>{item.t}</span>;
}

/** A dotted leader: real full stops, clipped to the span the form gives it. */
function Leader({ item }) {
    const size = item.size ?? TYPE.body.size;
    const pitch = item.pitch ?? PITCH.body;
    return (
        <span
            aria-hidden
            data-leader
            data-leader-pitch={pitch}
            style={{
                ...textStyle(size, TYPE.body.weight),
                top: topFor(item.y, size),
                left: item.from + X_SHIFT,
                width: Math.max(0, item.to - item.from),
                overflow: 'hidden',
                letterSpacing: LEADER.letterSpacing(size, pitch),
            }}
        >
            {LEADER_TEXT}
        </span>
    );
}

/**
 * A typed answer: larger than the form's own text, and shrinking toward
 * `minSize` only when the blank is too short for it.
 */
function Value({ item, text }) {
    const lift = item.lift ?? VALUE_LIFT;
    const inset = item.inset ?? VALUE_INSET;
    const maxWidth = Math.max(0, item.to - item.from - inset - 2);
    const [ref, size] = useFitFontSize(
        item.size ?? VALUE_TYPE.size,
        item.minSize ?? VALUE_TYPE.minSize,
        [text, maxWidth],
    );
    if (!text) return null;
    return (
        <span
            ref={ref}
            data-field-value
            data-filled="true"
            style={{
                ...textStyle(size, VALUE_TYPE.weight),
                top: topFor(item.y - lift, size),
                left: item.from + inset + X_SHIFT,
                maxWidth,
                textShadow: VALUE_INK_SHADOW,
                overflow: 'hidden',
                textOverflow: item.size ? 'clip' : 'ellipsis',
            }}
        >
            {text}
        </span>
    );
}

/** A rectangle the form prints, drawn with the original's 0.72 pt stroke. */
function Box({ item }) {
    return (
        <span
            aria-hidden
            style={{
                position: 'absolute',
                left: item.x + X_SHIFT,
                top: item.y,
                width: item.w,
                height: item.h,
                border: '0.72px solid #111',
                boxSizing: 'border-box',
            }}
        />
    );
}

/** The printed tick box, with a ✓ in it when the document is ticked. */
function Tick({ item, ticked }) {
    const size = 13;
    return (
        <>
            <Box item={item} />
            {ticked ? (
                <span
                    data-field-value
                    data-filled="true"
                    style={{
                        ...textStyle(size, 700),
                        top: topFor(item.y + item.h * 0.8, size),
                        left: item.x + X_SHIFT,
                        width: item.w,
                        textAlign: 'center',
                    }}
                >
                    ✓
                </span>
            ) : null}
        </>
    );
}

/**
 * The declaration's third line carries two answers in the middle of a
 * sentence, so it is one flowing line that steps its size back only if the
 * answers push it past the margin.
 */
function FlowLine({ item, thana, district }) {
    const [ref, size] = useFitFontSize(TYPE.decl.size, 10.2, [thana, district, item.width]);
    const tight = { whiteSpace: 'pre' };
    return (
        <span
            ref={ref}
            data-flow-line
            style={{
                ...textStyle(size, TYPE.decl.weight),
                top: topFor(item.y, size),
                left: item.x + X_SHIFT,
                width: item.width,
                overflow: 'hidden',
                ...tight,
            }}
        >
            {',উপজেলাঃ '}
            <span data-inline-value style={{ textShadow: VALUE_INK_SHADOW, fontWeight: 600 }}>{thana || '          '}</span>
            {', জেলাঃ '}
            <span data-inline-value style={{ textShadow: VALUE_INK_SHADOW, fontWeight: 600 }}>{district || '          '}</span>
            {` ${FLOW_TAIL}`}
        </span>
    );
}

function Emblem() {
    return (
        <img
            src="/images/ec-logo.png"
            alt=""
            aria-hidden
            style={{
                position: 'absolute',
                left: EMBLEM.left + X_SHIFT,
                top: EMBLEM.top,
                width: EMBLEM.width,
                height: EMBLEM.height,
                opacity: 0.6,
            }}
        />
    );
}

function Item({ item, d }) {
    switch (item.k) {
        case 'text': return <Text item={item} />;
        case 'leader': return <Leader item={item} />;
        case 'value': return <Value item={item} text={d[item.field]} />;
        case 'box': return <Box item={item} />;
        case 'tick': return <Tick item={item} ticked={Boolean(d[item.field])} />;
        case 'emblem': return <Emblem />;
        case 'rule':
            return (
                <span
                    aria-hidden
                    style={{
                        position: 'absolute',
                        left: item.x + X_SHIFT,
                        top: item.y,
                        width: item.w,
                        height: item.h,
                        background: '#111',
                    }}
                />
            );
        case 'flow': return <FlowLine item={item} thana={d.thana} district={d.district} />;
        default: return null;
    }
}

function Page({ items, d }) {
    return (
        <div data-voter-inclusion-page style={PAGE_STYLE}>
            {items.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Item key={`${item.k}-${index}`} item={item} d={d} />
            ))}
        </div>
    );
}

function VoterInclusionFormPreview({ form }, ref) {
    const d = buildVoterInclusionForm(form);

    return (
        <div ref={ref} data-voter-inclusion-document style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Page items={PAGE1} d={d} />
            <Page items={PAGE2} d={d} />
            <Page items={PAGE3} d={d} />
        </div>
    );
}

export default forwardRef(VoterInclusionFormPreview);
